import React from "react";
import { Pressable, StyleSheet, View } from "react-native";
import {
  ArrowSquareOutIcon,
  DownloadSimpleIcon,
  FileTextIcon,
  ImageIcon,
  VideoCameraIcon,
} from "@phosphor-icons/react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import type { ActivityAttachment } from "../types/reviewTypes";
import { isAttachmentOpenAction } from "../utils/attachments";

interface AttachmentRowProps {
  attachment: ActivityAttachment;
  isBusy: boolean;
  isFirst: boolean;
  onDownload: (attachment: ActivityAttachment) => void;
  onOpen: (attachment: ActivityAttachment) => void;
}

function getAttachmentIcon(attachment: ActivityAttachment) {
  if (attachment.kind === "link") {
    return { Icon: ArrowSquareOutIcon, color: colors.brand[600] };
  }
  if (attachment.kind === "video") {
    return { Icon: VideoCameraIcon, color: "#F97316" };
  }
  if (attachment.kind === "image") {
    return { Icon: ImageIcon, color: "#15727D" };
  }

  switch (attachment.fileType.toLowerCase()) {
    case "doc":
    case "docx":
      return { Icon: FileTextIcon, color: colors.brand[600] };
    case "pdf":
      return { Icon: FileTextIcon, color: colors.error[600] };
    default:
      return { Icon: FileTextIcon, color: colors.grey[500] };
  }
}

function AttachmentRow({ attachment, isBusy, isFirst, onDownload, onOpen }: AttachmentRowProps) {
  const { Icon, color: iconColor } = getAttachmentIcon(attachment);
  const useOpenAction = isAttachmentOpenAction(attachment);
  const actionText = useOpenAction ? "Open" : "Download";
  const accessibilityLabel = useOpenAction ? `Open ${attachment.fileName}` : `Download ${attachment.fileName}`;
  const metadata = [attachment.fileType, attachment.fileSize, attachment.duration].filter((meta): meta is string =>
    Boolean(meta)
  );

  return (
    <View style={[styles.row, !isFirst && styles.rowSeparator]}>
      <View style={[styles.iconSquare, attachment.kind === "image" && styles.imageIconSquare]}>
        <Icon size={20} color={iconColor} weight="regular" />
      </View>

      <View style={styles.middle}>
        <CustomText
          text={attachment.fileName}
          useTranslationText={false}
          size="s"
          weight="medium"
          style={styles.fileName}
          numberOfLines={1}
        />
        <View style={styles.metaRow}>
          {metadata.map((meta, index) => (
            <React.Fragment key={meta}>
              {index > 0 && <View style={styles.metaDivider} />}
              <CustomText
                text={index === 0 ? meta.toUpperCase() : meta}
                useTranslationText={false}
                size="xs"
                style={styles.metaText}
              />
            </React.Fragment>
          ))}
        </View>
      </View>

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        disabled={isBusy}
        onPress={() => (useOpenAction ? onOpen(attachment) : onDownload(attachment))}
        style={[styles.actionButton, isBusy && styles.disabled]}>
        {useOpenAction ? (
          <ArrowSquareOutIcon size={14} color={colors.grey[700]} weight="regular" />
        ) : (
          <DownloadSimpleIcon size={14} color={colors.grey[700]} weight="regular" />
        )}
        <CustomText
          text={isBusy ? "Working" : actionText}
          useTranslationText={false}
          size="xs"
          weight="medium"
          style={styles.actionText}
        />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  rowSeparator: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[100],
  },
  iconSquare: {
    width: 40,
    height: 40,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    backgroundColor: colors.grey[50],
  },
  imageIconSquare: {
    backgroundColor: colors.grey[100],
  },
  middle: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  fileName: {
    color: colors.grey[900],
  },
  metaRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  metaText: {
    color: colors.grey[500],
  },
  metaDivider: {
    width: 1,
    height: 10,
    backgroundColor: colors.grey[200],
  },
  actionButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    flexShrink: 0,
  },
  disabled: {
    opacity: 0.6,
  },
  actionText: {
    color: colors.grey[700],
  },
});

export default AttachmentRow;
